// Atmospheric lighting: hearth glow, candle flickers, ambient dust motes.
import { PALETTE as C } from "./palette.js";
import { TILE, ROOM_W, ROOM_H } from "./room.js";

export function drawLighting(scene) {
  // ═══ Hearth glow ═══
  const hearthCX = ROOM_W - TILE * 2.25;
  const hearthCY = TILE * 5;

  const glowLarge = scene.add.circle(hearthCX, hearthCY, TILE * 5, C.firewarm, 0.08);
  const glowMid   = scene.add.circle(hearthCX, hearthCY, TILE * 3, C.fireBright, 0.1);
  const glowCore  = scene.add.circle(hearthCX, hearthCY, TILE * 1.2, C.fireBright, 0.22);

  // Flickering logs inside the hearth (drawn on top of the stone)
  const flame1 = scene.add.ellipse(hearthCX, hearthCY - TILE * 0.2, TILE * 0.4, TILE * 0.7, C.firewarm, 0.85);
  const flame2 = scene.add.ellipse(hearthCX - TILE * 0.3, hearthCY, TILE * 0.25, TILE * 0.5, C.fireBright, 0.9);
  const flame3 = scene.add.ellipse(hearthCX + TILE * 0.3, hearthCY + TILE * 0.1, TILE * 0.3, TILE * 0.5, C.firewarm, 0.95);

  // Flicker tweens: independent timings so the fire feels alive.
  [glowLarge, glowMid, glowCore].forEach((g, i) => {
    scene.tweens.add({
      targets: g,
      alpha: g.alpha * 1.6,
      yoyo: true,
      repeat: -1,
      duration: 900 + i * 280,
      ease: "Sine.inOut",
    });
  });
  [flame1, flame2, flame3].forEach((f, i) => {
    scene.tweens.add({
      targets: f,
      scaleY: 1.15,
      scaleX: 0.92,
      yoyo: true,
      repeat: -1,
      duration: 350 + i * 120,
      ease: "Sine.inOut",
    });
    scene.tweens.add({
      targets: f,
      alpha: f.alpha * 0.7,
      yoyo: true,
      repeat: -1,
      duration: 220 + i * 60,
      ease: "Sine.inOut",
    });
  });

  // ═══ Candles on tables ═══
  const candlePositions = [
    { x: TILE * 4,  y: TILE * 7 - 7 },
    { x: TILE * 9,  y: TILE * 8.5 - 7 },
    { x: TILE * 14, y: TILE * 7.5 - 7 },
  ];
  for (const p of candlePositions) {
    const halo = scene.add.circle(p.x, p.y, TILE * 1.4, C.candleFlame, 0.1);
    const flame = scene.add.ellipse(p.x, p.y, 5, 7, C.candleFlame, 0.95);
    scene.tweens.add({
      targets: halo,
      alpha: 0.16,
      yoyo: true,
      repeat: -1,
      duration: 1200 + Math.random() * 400,
      ease: "Sine.inOut"
    });
    scene.tweens.add({
      targets: flame,
      scaleY: 1.2,
      yoyo: true,
      repeat: -1,
      duration: 180 + Math.random() * 120,
      ease: "Sine.inOut"
    });
  }

  // ═══ Dust motes floating ═══
  for (let i = 0; i < 18; i++) {
    const mote = scene.add.circle(
      TILE + Math.random() * (ROOM_W - TILE * 2),
      TILE * 2 + Math.random() * (ROOM_H - TILE * 4),
      0.8,
      C.candleFlame,
      0.35
    );
    const driftX = 20 + Math.random() * 40;
    const driftY = 10 + Math.random() * 20;
    const duration = 6000 + Math.random() * 4000;
    scene.tweens.add({
      targets: mote,
      x: mote.x + driftX,
      y: mote.y - driftY,
      alpha: 0,
      duration,
      repeat: -1,
      onRepeat: () => {
        mote.x = TILE + Math.random() * (ROOM_W - TILE * 2);
        mote.y = ROOM_H - TILE - Math.random() * TILE * 3;
        mote.alpha = 0.35;
      }
    });
  }

  // ═══ Soft vignette around the edges ═══
  const vg = scene.add.graphics();
  vg.fillStyle(C.bg, 0.55);
  // Top
  vg.fillRect(0, 0, ROOM_W, TILE * 0.5);
  // Bottom
  vg.fillRect(0, ROOM_H - TILE * 0.5, ROOM_W, TILE * 0.5);
  // Left
  vg.fillRect(0, 0, TILE * 0.5, ROOM_H);
  // Right
  vg.fillRect(ROOM_W - TILE * 0.5, 0, TILE * 0.5, ROOM_H);
}
